// src/hooks/useTpnCart.ts
import { useMemo } from 'react';
import { useAppSelector } from '../redux/hooks';
import { shouldShowCartIcon } from '../utils/tpnUtils';
import { checkIfRestaurantClosed, formatStartTimesToEST, isInBreakHours } from '../utils/reservationUtils';
import { PaymentPreferences } from '../types/restaurant';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Custom hook for determining cart icon visibility based on TPN configuration
 * 
 * @param showCartIcon Whether cart icon is enabled in widget settings
 * @returns boolean indicating if cart icon should be shown
 */
export const useTpnCart = (showCartIcon: boolean): boolean => {
  // Get TPN configuration from Redux store
  const tpnConfig = useAppSelector((state) => state?.tpnConfig?.data);
  
  // Get selected restaurant from Redux store
  const selectedRestaurant = useAppSelector((state) => state?.restaurant?.selectedRestaurant);
  
  // Get table availability state from Redux store
  const { 
    loading: tableLoading, 
    time_slots 
  } = useAppSelector((state) => state.tableAvailability);
  
  // Get site content for restaurant hours
  const siteContent = useAppSelector((state) => state.siteContent);
  
  // Memoize the cart visibility logic with complete reservation checks
  const shouldShowCart = useMemo(() => {
    // Check 0: isAddToCartEnabled (HIGHEST PRIORITY - from backend)
    let paymentPrefs: PaymentPreferences | null = null;
    if (selectedRestaurant?.payment_perferences) {
      try {
        paymentPrefs = typeof selectedRestaurant.payment_perferences === 'string'
          ? JSON.parse(selectedRestaurant.payment_perferences)
          : selectedRestaurant.payment_perferences;
      } catch (error) {
        // Fail silently
      }
    }
    
    const isAddToCartEnabled = paymentPrefs?.isAddToCartEnabled ?? true;
    if (!isAddToCartEnabled) {
      return false;
    }
    
    // Check 1: Restaurant must be open today
    const todayDate = new Date().toISOString().split('T')[0];
    const isRestaurantClosed = checkIfRestaurantClosed(todayDate, siteContent);
    if (isRestaurantClosed) {
      return false;
    }
    
    // Check 1.1: Current ET time MUST be strictly within today's operating hours from JSON
    const nowET = dayjs().tz('America/New_York');
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDayName = dayNames[nowET.day()];
    const hoursData = (siteContent?.data as any)?.reservation?.info?.hours?.[currentDayName];

    if (hoursData && hoursData.is_closed) {
      return false;
    }

    if (hoursData) {
      const currentDateStr = nowET.format('YYYY-MM-DD');

      const hasFirstHalf = Boolean(
        hoursData.firstHalf?.start?.trim() && hoursData.firstHalf?.end?.trim()
      );
      const hasSecondHalf = Boolean(
        hoursData.secondHalf?.start?.trim() && hoursData.secondHalf?.end?.trim()
      );

      if (!hasFirstHalf && !hasSecondHalf) {
        return false;
      }

      let isWithinHours = false;

      const checkShift = (startStr: string, endStr: string) => {
        const start = dayjs.tz(`${currentDateStr} ${startStr.trim()}`, 'America/New_York');
        const end = dayjs.tz(`${currentDateStr} ${endStr.trim()}`, 'America/New_York');
        return (nowET.isAfter(start) || nowET.isSame(start)) && (nowET.isBefore(end) || nowET.isSame(end));
      };

      // 1. Check First Half shift
      if (hasFirstHalf && checkShift(hoursData.firstHalf.start, hoursData.firstHalf.end)) {
        isWithinHours = true;
      }

      // 2. Check Second Half shift
      if (!isWithinHours && hasSecondHalf && checkShift(hoursData.secondHalf.start, hoursData.secondHalf.end)) {
        isWithinHours = true;
      }

      if (!isWithinHours) {
        return false;
      }
    }

    // Check 1.5: Hide cart during break hours
    const inBreak = isInBreakHours(siteContent);
    if (inBreak) {
      return false;
    }
    
    // Check 2: TPN Configuration must exist
    const hasTpnConfig = shouldShowCartIcon(showCartIcon, tpnConfig);
    if (!hasTpnConfig) {
      return false;
    }
    
    // Check 4: Time slots check (Skipped while still loading to prevent false negatives)
    if (!tableLoading) {
      if (!time_slots || time_slots.length === 0) {
        return false;
      }
      
      const filteredSlots = formatStartTimesToEST(time_slots, todayDate);
      if (filteredSlots.length === 0) {
        return false;
      }
    }
    
    return true;
  }, [selectedRestaurant, siteContent, showCartIcon, tpnConfig, tableLoading, time_slots]);
  
  return shouldShowCart;
};

/**
 * Custom hook for getting TPN configuration status
 */
export const useTpnStatus = () => {
  const tpnConfig = useAppSelector((state) => state?.tpnConfig?.data);
  
  return useMemo(() => {
    return {
      isActive: !!(tpnConfig?.tpn_config && tpnConfig.tpn_config.length > 0),
      configCount: tpnConfig?.tpn_config?.length || 0,
      hasConfig: !!tpnConfig,
      config: tpnConfig
    };
  }, [tpnConfig]);
};